import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSettings } from '../lib/api';
import { useLang } from '../lib/lang';

/**
 * Vertical WhatsApp pin attached to the right edge.
 * - Pulses subtly to attract attention
 * - On hover: expands + shows tooltip ("Une question ? Contactez-nous !")
 * - Click: opens WhatsApp chat
 * - Auto-hides if not enabled in admin
 */
export default function WhatsAppBadge() {
  const { t } = useLang();
  const [config, setConfig] = useState({ enabled: false, number: '', message: '' });
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    getSettings()
      .then((r) => {
        const s = r.data.settings || {};
        setConfig({
          enabled: s.whatsapp_enabled === true,
          number: (s.whatsapp_number || '').replace(/\D/g, ''),
          message: s.whatsapp_message || '',
        });
      })
      .catch(() => {});
  }, []);

  if (!config.enabled || !config.number) return null;

  const wa = `https://wa.me/${config.number}${config.message ? `?text=${encodeURIComponent(config.message)}` : ''}`;
  const tooltip = t('whatsappPrompt') || 'Une question ? Contactez-nous !';

  return (
    <div
      className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="whatsapp-badge"
    >
      {/* Tooltip on hover */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.92 }}
            animate={{ opacity: 1, x: -8, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.92 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="mr-1 relative pointer-events-none"
          >
            <div className="bg-[#0B3D2E] border border-[hsl(var(--gold)/0.5)] rounded-xl px-4 py-2.5 shadow-[0_8px_28px_rgba(0,0,0,0.4)] gold-glow whitespace-nowrap">
              <p className="text-xs font-semibold text-cream tracking-wide">{tooltip}</p>
              <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-[#0B3D2E] border-r border-t border-[hsl(var(--gold)/0.5)] rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Vertical WhatsApp pin */}
      <a
        href={wa}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-badge-link"
        aria-label={tooltip}
        className="group relative block"
      >
        {/* Pulse halo */}
        <span className="absolute inset-0 rounded-l-2xl bg-[#25D366] animate-ping opacity-25 pointer-events-none" />

        <motion.div
          animate={{ width: hovered ? 48 : 38 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="relative flex flex-col items-center justify-between gap-2 py-3 rounded-l-2xl bg-gradient-to-b from-[#25D366] to-[#128C7E] shadow-[-4px_0_18px_rgba(37,211,102,0.4)] hover:shadow-[-6px_0_28px_rgba(37,211,102,0.6)] cursor-pointer overflow-hidden"
          style={{ height: 168 }}
        >
          {/* WhatsApp SVG icon at top */}
          <div className="shrink-0">
            <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </div>

          {/* Vertical text "WhatsApp" */}
          <span
            className="font-display font-bold text-white tracking-[0.18em] text-[10px] uppercase select-none whitespace-nowrap drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            WhatsApp
          </span>

          {/* Pulse dot at bottom */}
          <div className="shrink-0 relative w-2 h-2">
            <span className="absolute inset-0 bg-white/95 rounded-full" />
            <span className="absolute inset-0 bg-white/60 rounded-full animate-ping" />
          </div>
        </motion.div>
      </a>
    </div>
  );
}
